import { useState, useCallback, useEffect, useRef } from "react";

import { config } from "../../config";
import { useHttpClient } from "../Hooks/http-hook";
import dedupeStoreFetch from "../util/dedupe-store-fetch";

const useStoreSettings = props => {
    const [storeSettings, setStoreSettings] = useState(null);
    const [isUpdatedStoreSettings, setIsUpdatedStoreSettings] = useState(false);
    const [HideStoreSettingsSkeleton, setHideStoreSettingsSkeleton] = useState(false);
    const [settingsStoreUsername, setSettingsStoreUsername] = useState('');
    const { error, sendRequest, clearError } = useHttpClient();
    const cacheRef = useRef({
        isUpdatedStoreSettings: false,
        settingsStoreUsername: '',
        storeSettings: null,
    });
    const pendingFetchesRef = useRef(new Map());

    cacheRef.current = {
        isUpdatedStoreSettings,
        settingsStoreUsername,
        storeSettings,
    };

    const updateStoreSettings = (newStoreSettings) => {
        setStoreSettings(newStoreSettings);
        setIsUpdatedStoreSettings(true);
    };

    const fetchStoreSettings = useCallback(async (username, { force = false } = {}) => {
        return dedupeStoreFetch({
            key: username,
            force,
            cacheRef,
            pendingRef: pendingFetchesRef,
            isCacheHit: (cache) => (
                cache.isUpdatedStoreSettings && cache.settingsStoreUsername === username
            ),
            getCachedValue: (cache) => cache.storeSettings,
            runRequest: async () => {
                try {
                    const responseData = await sendRequest(
                        `${config.API_URL}/storeSettings/${username}`
                    );
                    clearError();
                    updateStoreSettings(responseData);
                    setSettingsStoreUsername(username);
                    return responseData;
                } catch (err) {
                    setIsUpdatedStoreSettings(true);
                    setSettingsStoreUsername(username);
                    console.error(err);
                    return null;
                }
            },
        });
    }, [sendRequest, clearError]);

    const reloadStoreSettings = useCallback(
        (username) => fetchStoreSettings(username, { force: true }),
        [fetchStoreSettings]
    );

    const removeStoreSettings = useCallback(() => {
        pendingFetchesRef.current.clear();
        setStoreSettings(null);
        setSettingsStoreUsername('');
        setIsUpdatedStoreSettings(false);
        setHideStoreSettingsSkeleton(false);
    }, []);

    useEffect(() => {
        const storedData = JSON.parse(localStorage.getItem('storec-userData'));
        if (storedData && storedData.username && props.role === "store" && !isUpdatedStoreSettings) {
            fetchStoreSettings(storedData.username);
        }
    }, [props.role]);

    return {
        storeSettings,
        isUpdatedStoreSettings,
        fetchStoreSettings,
        reloadStoreSettings,
        HideStoreSettingsSkeleton,
        StoreSettingsError: error,
        removeStoreSettings,
        setHideStoreSettingsSkeleton,
        settingsStoreUsername,
    };
};

export default useStoreSettings;
